import React from 'react'
import PropTypes from 'prop-types'
import { css } from '@emotion/core'
import { ArrowCaret, Menu, MenuClose } from '@fs/zion-icon'
import { Portal } from '@fs/zion-ui/quarks-authorized-use-only'
import { useRandomId } from '@fs/zion-frontend-friends'
import ExperienceNavTrigger from './ExperienceNavTrigger'
import NAV_ROW_GLYPHS from './navRowGlyphs'
import navigationItemPropType, { navigationParentPropType } from './navigationItemPropType'
import { NAV_ROW_KINDS } from './navigationModel'
import useNavigationCard from './useNavigationCard'
import useNavRowLabels from './useNavRowLabels'
import {
  EDGE_TAB_PLACEMENT,
  NAV_INLINE_START_CSS,
  NAV_SURFACE_BACKGROUND,
  NAV_SURFACE_COLOR,
  NAV_SURFACE_HOVER_OVERLAY,
  NAV_SURFACE_Z_INDEX,
  SAFE_AREA_LEFT_CSS,
  TOP_CORNER_PLACEMENT,
  focusRingCss,
  leaveRowCss,
  rowGlyphCss,
} from './experienceNavSurface.styles'
import { experienceNavControlProps } from './experienceNavControlMarker'

// The row for the page the visitor is already on goes nowhere; choosing it
// only dismisses the card (see selectNavItemAction).
const dismissOnly = () => {
  /* nothing to do beyond closing */
}

// The card is centered in the viewport, so the reachable height it may grow
// into is symmetric: whichever safe-area inset is larger sets the margin at
// both ends.
const NAV_DRAWER_EDGE_MARGIN_CSS = 'max(24px, env(safe-area-inset-top, 0px), env(safe-area-inset-bottom, 0px))'
export const NAV_DRAWER_AVAILABLE_HEIGHT_CSS = `calc(100vh - 2 * ${NAV_DRAWER_EDGE_MARGIN_CSS})`

// The card clears the edge by the same gutter the experiences reserve.
export const NAV_DRAWER_LEFT_CSS = `calc(${SAFE_AREA_LEFT_CSS} + ${NAV_INLINE_START_CSS})`

const overlayCss = (isOpen) => css`
  position: fixed;
  inset: 0;
  z-index: ${NAV_SURFACE_Z_INDEX};
  visibility: ${isOpen ? 'visible' : 'hidden'};
  pointer-events: ${isOpen ? 'auto' : 'none'};
  transition: visibility 0s ${isOpen ? '0s' : '0.25s'};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const backdropCss = (isOpen) => css`
  position: absolute;
  inset: 0;
  border: none;
  padding: 0;
  background: rgba(0, 0, 0, 0.48);
  opacity: ${isOpen ? 1 : 0};
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  transition: opacity 0.25s ease;
  cursor: pointer;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

// A single centered card in both orientations: at 1080x1920 an earlier
// top-anchored tray put its first actions above comfortable reach, and at 1920x1080 it ran
// the full height of the screen for three actions. Anchoring the physical
// left edge (even in RTL) keeps it spatially tied to the trigger that opened
// it.
/* eslint-disable @fs/zion/logical-over-directional */
const drawerCss = (isOpen) => css`
  position: absolute;
  top: 50%;
  left: ${NAV_DRAWER_LEFT_CSS};
  box-sizing: border-box;
  width: min(360px, calc(100vw - 2 * ${NAV_DRAWER_EDGE_MARGIN_CSS}));
  max-height: ${NAV_DRAWER_AVAILABLE_HEIGHT_CSS};
  padding: 12px 16px 20px;
  border-radius: 32px;
  background: ${NAV_SURFACE_BACKGROUND};
  color: ${NAV_SURFACE_COLOR};
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.28);
  opacity: ${isOpen ? 1 : 0};
  transform: translate(${isOpen ? '0' : '-16px'}, -50%);
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`
/* eslint-enable @fs/zion/logical-over-directional */

// Sits on its own row above the actions rather than overlapping them, so no
// action has to reserve space around it.
const closeButtonCss = css`
  align-self: flex-end;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  padding: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover,
  &:focus-visible {
    background: ${NAV_SURFACE_HOVER_OVERLAY};
  }

  ${focusRingCss};
`

const actionListCss = css`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
`

// Separates the three blocks the card is built from - the level above, this
// level's list, and the way out of the experience. Inset to the row text so
// it reads as a grouping rule rather than a full-bleed cut across the card.
const dividerCss = css`
  flex-shrink: 0;
  border: none;
  border-block-start: 1px solid rgba(0, 0, 0, 0.12);
  margin: 8px 12px;
`

// Names the level the listed items belong to ("Sports"), so a section index
// needs no repeated prefix on every row.
const sectionHeadingCss = css`
  flex-shrink: 0;
  margin: 8px 0;
  padding-inline: 12px;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
`

const actionButtonCss = css`
  min-height: 48px;
  width: 100%;
  border: none;
  border-radius: 12px;
  padding: 8px 12px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 18px;
  font-weight: 400;
  text-align: start;
  ${rowGlyphCss};

  &:hover,
  &:focus-visible {
    background: ${NAV_SURFACE_HOVER_OVERLAY};
  }

  ${focusRingCss};
`

// The row for the page you are already on. It stays in the list so the
// section index is complete, and says so rather than pretending to be a
// destination.
const currentActionCss = css`
  font-weight: 700;
  cursor: default;
`

// Every row the card holds is the same shape - a glyph and a name that goes
// somewhere - so one component draws them all, and the three blocks below only
// say which rows they hold.
const NavRow = ({ glyph, label, ariaLabel = null, onClick, current = false, leave = false }) => (
  <li>
    <button
      type="button"
      css={[actionButtonCss, current && currentActionCss, leave && leaveRowCss]}
      onClick={onClick}
      aria-label={ariaLabel ?? label}
      aria-current={current ? 'page' : undefined}
    >
      {glyph}
      {label}
    </button>
  </li>
)

NavRow.propTypes = {
  glyph: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  /** Overrides the visible label as the accessible name. */
  ariaLabel: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  /** The page the visitor is already on: listed for completeness, marked, inert. */
  current: PropTypes.bool,
  /** Leave Kiosk's warning tint - the one row that leaves the kiosk entirely. */
  leave: PropTypes.bool,
}

// The card's three blocks, split out so the drawer itself reads as the card's
// shape rather than as every row it can hold. Each takes handlers already
// wrapped in the close-then-act behaviour every row shares.

/**
 * The one way up: the level above wearing whichever face it has - the
 * app-declared parent, a registration's own name, or the generic Back where
 * nothing names the level above (Picture Our Heritage). A registration under
 * a declared level folds into that row (see buildNavigationIndex), so Back
 * and a parent never sit here together.
 */
const LevelAboveRows = ({ parentItem, showBack, backLabel, backAriaLabel, onSelectParent, onBack }) => (
  <ul css={actionListCss}>
    {showBack && (
      <NavRow glyph={NAV_ROW_GLYPHS[NAV_ROW_KINDS.BACK]} label={backLabel} ariaLabel={backAriaLabel} onClick={onBack} />
    )}
    {parentItem && (
      <NavRow
        glyph={NAV_ROW_GLYPHS[NAV_ROW_KINDS.PARENT]}
        label={parentItem.label}
        ariaLabel={parentItem.ariaLabel}
        onClick={onSelectParent}
      />
    )}
  </ul>
)

LevelAboveRows.propTypes = {
  parentItem: navigationParentPropType,
  showBack: PropTypes.bool.isRequired,
  backLabel: PropTypes.string.isRequired,
  backAriaLabel: PropTypes.string.isRequired,
  onSelectParent: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
}

/** This level in full, the visitor's own row included and marked. */
const SectionIndex = ({ sectionLabel, headingId, navItems, onSelectNavItem }) => (
  <>
    {sectionLabel && (
      <h2 css={sectionHeadingCss} id={headingId}>
        {sectionLabel}
      </h2>
    )}
    {navItems.length > 0 && (
      <ul css={actionListCss} aria-labelledby={sectionLabel ? headingId : undefined}>
        {navItems.map((item) => (
          <NavRow
            key={item.id}
            glyph={<span aria-hidden>{item.icon}</span>}
            label={item.label}
            ariaLabel={item.ariaLabel}
            current={Boolean(item.current)}
            // The current page is listed for completeness, so its row only
            // dismisses the card - re-pushing the route would reset the very
            // screen behind it.
            onClick={onSelectNavItem(item.current ? null : item)}
          />
        ))}
      </ul>
    )}
  </>
)

SectionIndex.propTypes = {
  sectionLabel: PropTypes.string,
  headingId: PropTypes.string.isRequired,
  navItems: PropTypes.arrayOf(navigationItemPropType).isRequired,
  /** Curried: takes the row (null for the current one) and returns its click handler. */
  onSelectNavItem: PropTypes.func.isRequired,
}

/** The ways out of the experience entirely. */
const ExitRows = ({ showHome, showKidsReturn, showLeaveKiosk, labels, onHome, onKidsReturn, onLeaveKiosk }) => (
  <ul css={actionListCss}>
    {showHome && (
      <NavRow glyph={NAV_ROW_GLYPHS[NAV_ROW_KINDS.HOME]} label={labels.allExperiencesLabel} onClick={onHome} />
    )}
    {showKidsReturn && (
      <NavRow glyph={NAV_ROW_GLYPHS[NAV_ROW_KINDS.KIDS_RETURN]} label={labels.kidsReturnLabel} onClick={onKidsReturn} />
    )}
    {showLeaveKiosk && (
      <NavRow
        glyph={NAV_ROW_GLYPHS[NAV_ROW_KINDS.LEAVE_KIOSK]}
        label={labels.leaveKioskLabel}
        onClick={onLeaveKiosk}
        leave
      />
    )}
  </ul>
)

ExitRows.propTypes = {
  showHome: PropTypes.bool.isRequired,
  showKidsReturn: PropTypes.bool.isRequired,
  showLeaveKiosk: PropTypes.bool.isRequired,
  labels: PropTypes.shape({
    allExperiencesLabel: PropTypes.string.isRequired,
    kidsReturnLabel: PropTypes.string.isRequired,
    leaveKioskLabel: PropTypes.string.isRequired,
  }).isRequired,
  onHome: PropTypes.func.isRequired,
  onKidsReturn: PropTypes.func.isRequired,
  onLeaveKiosk: PropTypes.func.isRequired,
}

export default function ExperienceNavigationDrawer({
  triggerPlacement,
  headerVisible,
  backAriaLabel,
  showBack,
  showHome,
  showKidsReturn,
  showLeaveKiosk,
  parentItem = null,
  sectionLabel = null,
  navItems,
  onBack,
  onSelectParent,
  onSelectNavItem,
  onHome,
  onKidsReturn,
  onLeaveKiosk,
}) {
  const labels = useNavRowLabels()
  const { isOpen, open, close: closeDrawer, triggerRef, cardRef, closeRef } = useNavigationCard()
  const isEdgeTab = triggerPlacement === EDGE_TAB_PLACEMENT
  // Generated rather than fixed: this component makes no promise about being
  // the only one on the page, and a duplicated id silently breaks the
  // heading's aria-labelledby link to its list.
  const sectionHeadingId = useRandomId()

  // The card is three stacked blocks - the level above, this level's index,
  // and the ways out of the experience - and any of them can be absent (an
  // experience with no sub-levels has no section; a single-app kiosk has no
  // Home). A rule is drawn only where two present blocks meet.
  const hasLevelAbove = Boolean(parentItem) || showBack
  const hasSection = Boolean(sectionLabel) || navItems.length > 0
  const hasExits = showHome || showKidsReturn || showLeaveKiosk
  const ruleAfterLevelAbove = hasLevelAbove && hasSection
  const ruleBeforeExits = hasExits && (hasLevelAbove || hasSection)

  const selectAction = (action) => () => {
    closeDrawer()
    action()
  }

  // Curried so the index can ask for a row's handler by row. The current row's
  // only job is to dismiss the card.
  const selectNavItemAction = (item) => selectAction(item ? () => onSelectNavItem(item) : dismissOnly)

  return (
    <>
      <ExperienceNavTrigger
        ref={triggerRef}
        placement={triggerPlacement}
        headerVisible={headerVisible}
        onClick={open}
        aria-label={labels.openLabel}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        {isEdgeTab ? <ArrowCaret direction="forward" size="sm" aria-hidden /> : <Menu size="sm" aria-hidden />}
      </ExperienceNavTrigger>
      <Portal>
        <div css={overlayCss(isOpen)} aria-hidden={!isOpen} {...experienceNavControlProps}>
          <button
            type="button"
            css={backdropCss(isOpen)}
            onClick={() => closeDrawer()}
            aria-label={labels.closeLabel}
            tabIndex={-1}
            data-testid="experience-nav-backdrop"
          />
          <aside
            ref={cardRef}
            css={drawerCss(isOpen)}
            role="dialog"
            aria-modal="true"
            aria-label={labels.navigationLabel}
          >
            <button
              ref={closeRef}
              type="button"
              css={closeButtonCss}
              onClick={() => closeDrawer()}
              aria-label={labels.closeLabel}
            >
              <MenuClose size="sm" aria-hidden />
            </button>
            <nav aria-label={labels.navigationLabel}>
              {hasLevelAbove && (
                <LevelAboveRows
                  parentItem={parentItem}
                  showBack={showBack}
                  backLabel={labels.backLabel}
                  backAriaLabel={backAriaLabel}
                  onSelectParent={selectAction(onSelectParent)}
                  onBack={selectAction(onBack)}
                />
              )}
              {ruleAfterLevelAbove && <hr css={dividerCss} />}
              <SectionIndex
                sectionLabel={sectionLabel}
                headingId={sectionHeadingId}
                navItems={navItems}
                onSelectNavItem={selectNavItemAction}
              />
              {ruleBeforeExits && <hr css={dividerCss} />}
              {hasExits && (
                <ExitRows
                  showHome={showHome}
                  showKidsReturn={showKidsReturn}
                  showLeaveKiosk={showLeaveKiosk}
                  labels={labels}
                  onHome={selectAction(onHome)}
                  onKidsReturn={selectAction(onKidsReturn)}
                  onLeaveKiosk={selectAction(onLeaveKiosk)}
                />
              )}
            </nav>
          </aside>
        </div>
      </Portal>
    </>
  )
}

ExperienceNavigationDrawer.propTypes = {
  // 'edge-tab' is the reach-friendly STATION pull-out; 'top-corner' is the
  // conventional WEB menu button.
  triggerPlacement: PropTypes.oneOf([EDGE_TAB_PLACEMENT, TOP_CORNER_PLACEMENT]).isRequired,
  headerVisible: PropTypes.bool.isRequired,
  /** The Back row's accessible name - the registration's own description, else the generic label. */
  backAriaLabel: PropTypes.string.isRequired,
  showBack: PropTypes.bool.isRequired,
  showHome: PropTypes.bool.isRequired,
  showKidsReturn: PropTypes.bool.isRequired,
  showLeaveKiosk: PropTypes.bool.isRequired,
  /** The level above this one, rendered as the card's top row. */
  parentItem: navigationParentPropType,
  /** Names the level `navItems` belong to, e.g. the open category. */
  sectionLabel: PropTypes.string,
  navItems: PropTypes.arrayOf(navigationItemPropType).isRequired,
  onBack: PropTypes.func.isRequired,
  onSelectParent: PropTypes.func.isRequired,
  onSelectNavItem: PropTypes.func.isRequired,
  onHome: PropTypes.func.isRequired,
  onKidsReturn: PropTypes.func.isRequired,
  onLeaveKiosk: PropTypes.func.isRequired,
}
